/**
 * Como o dado do contrato vira texto na tela (H04).
 *
 * ── Por que num arquivo só, e puro ────────────────────────────────────────
 *
 * As três colunas mostram os mesmos valores em formatos diferentes — a lista
 * mostra "14:32", o histórico mostra "ontem", o painel mostra "R$ 132.900". Se
 * cada componente formatasse por conta, a mesma oportunidade apareceria como
 * "R$ 132.900" à direita e "132900" no cartão, e ninguém notaria até um cliente
 * perguntar.
 *
 * Puro também porque é o tipo de código que erra em caso de borda — meia-noite,
 * valor zero, moeda que não é real — e caso de borda se prova sem navegador.
 */

/**
 * `quantia_micros` → "R$ 132.900,00".
 *
 * A API manda micros (milionésimos) porque centavo em ponto flutuante acumula
 * erro: somar `0.1 + 0.2` já não dá `0.3`, e uma soma de carteira com trezentas
 * oportunidades sai errada no fim. Inteiro não tem esse problema, e a divisão
 * só acontece aqui, na última linha antes da tela.
 */
export function dinheiro(
  valor: { quantia_micros: number; moeda: string } | null | undefined,
): string | null {
  if (!valor) return null;

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: valor.moeda,
    // Sem centavos: valor de veículo é sempre redondo, e ".00" em toda linha da
    // lista rouba espaço numa tela que já é apertada.
    maximumFractionDigits: 0,
  }).format(valor.quantia_micros / 1_000_000);
}

/**
 * `quantia_micros` → "R$ 890" ou "R$ 199,90": sem centavos só quando o valor é
 * redondo. Para a mensalidade do plano, que costuma ser redonda e aparece em
 * destaque, mas que não pode virar outro preço quando não é: "R$ 200" no lugar
 * de R$ 199,90 (#1160).
 */
export function dinheiroSemCentavosQuandoRedondo(
  valor: { quantia_micros: number; moeda: string } | null | undefined,
): string | null {
  if (!valor) return null;
  return valor.quantia_micros % 1_000_000 === 0 ? dinheiro(valor) : dinheiroComCentavos(valor);
}

/**
 * `quantia_micros` → "R$ 59,90": com centavos, sempre.
 *
 * Preço de plano e de adicional não é redondo (R$ 59,90 por usuário, R$ 2,99
 * por GB), e o `dinheiro` acima arredondaria para "R$ 60", que é outro preço.
 * Vale também para preço de tabela ("R$ 143,50"), em que o centavo existe e é o
 * que a pessoa vai repetir ao cliente.
 */
export function dinheiroComCentavos(
  valor: { quantia_micros: number; moeda: string } | null | undefined,
): string | null {
  if (!valor) return null;

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: valor.moeda,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(valor.quantia_micros / 1_000_000);
}

const UMA_CASA = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });

/**
 * 9.3 → "9,3", 10 → "10": no máximo uma casa, com vírgula.
 *
 * Para medida que não pede precisão, como o GB do armazenamento: duas casas
 * num limite de 10 GB seriam precisão que ninguém usa.
 */
export function comUmaCasa(valor: number): string {
  return UMA_CASA.format(valor);
}

const DIA_MES_ANO = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/** O instante → "02/09/2026", no fuso de quem olha. Para "desde quando", e não para hora. */
export function diaMesAno(iso: string): string {
  return DIA_MES_ANO.format(new Date(iso));
}

const HOJE = 'HOJE';

/**
 * A hora de uma mensagem na LISTA: curta, e o suficiente para localizar.
 *
 * Hoje mostra a hora; ontem mostra "ontem"; mais que isso mostra a data. É o
 * que o WhatsApp faz, e a razão é a mesma: a lista tem trinta linhas, e
 * "18/08/2026 14:32" em todas elas empurraria o nome do cliente para fora.
 */
export function horaCurta(instante: string | null | undefined, agora = Date.now()): string | null {
  if (!instante) return null;
  const data = new Date(instante);
  if (Number.isNaN(data.getTime())) return null;

  const dia = diaRelativo(data, new Date(agora));

  if (dia === HOJE) {
    return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(data);
  }
  if (dia === 'ONTEM') return 'ontem';

  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(data);
}

/** A hora exata, para o carimbo da bolha. */
export function horaDaMensagem(instante: string): string {
  return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(
    new Date(instante),
  );
}

/**
 * O divisor de dia do histórico: "Hoje", "Ontem", ou a data por extenso.
 *
 * Comparação por DIA DO CALENDÁRIO, não por diferença de horas. Uma mensagem
 * das 23h50 e outra das 00h10 têm vinte minutos de diferença e são de dias
 * diferentes — quem compara por 24 h junta as duas sob "hoje" e o histórico
 * fica mentindo na virada.
 */
export function divisorDeDia(instante: string, agora = Date.now()): string {
  const data = new Date(instante);
  const dia = diaRelativo(data, new Date(agora));

  if (dia === HOJE) return 'Hoje';
  if (dia === 'ONTEM') return 'Ontem';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: data.getFullYear() === new Date(agora).getFullYear() ? undefined : 'numeric',
  }).format(data);
}

function diaRelativo(data: Date, agora: Date): 'HOJE' | 'ONTEM' | 'ANTES' {
  const mesmoDia = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (mesmoDia(data, agora)) return HOJE;

  const ontem = new Date(agora);
  ontem.setDate(ontem.getDate() - 1);
  return mesmoDia(data, ontem) ? 'ONTEM' : 'ANTES';
}

/**
 * O telefone guardado no formato internacional (E.164), como gente lê:
 * "+5511987654321" vira "(11) 98765-4321". Número de fora do Brasil, ou fora
 * do formato, sai como veio: remontar produziria um telefone que não existe.
 */
export function telefoneParaLer(e164: string): string {
  const casou = /^\+55(\d{2})(\d{4,5})(\d{4})$/.exec(e164);
  if (!casou) return e164;
  return `(${casou[1]}) ${casou[2]}-${casou[3]}`;
}

/**
 * Iniciais para o avatar. Duas no máximo.
 *
 * Sem nome — o caso do contato que ainda não virou cadastro — devolve `?`, e
 * não a primeira letra do telefone mascarado. `•` como inicial fica ilegível.
 */
export function iniciais(nome: string | null | undefined): string {
  const partes = (nome ?? '').trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';

  const primeira = partes[0]?.[0] ?? '?';
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '';
  return (primeira + ultima).toUpperCase();
}

/**
 * O nome que a lista mostra quando o contato ainda não tem cadastro.
 *
 * O identificador já vem mascarado do servidor (`••••4321`), porque a lista é a
 * tela mais exposta do produto — dezenas de pessoas de uma vez, muitas vezes
 * com alguém olhando por cima do ombro.
 */
export function nomeParaExibir(
  cliente: { nome_exibicao: string | null; identificador_exibicao?: string | null } | null,
): string {
  return cliente?.nome_exibicao ?? cliente?.identificador_exibicao ?? 'Contato sem cadastro';
}

/**
 * A idade em uma palavra curta: `agora`, `12min`, `3h`, `5d`, `2sem`, `4mes`.
 *
 * ── Por que não reusar `idadeEmPalavras` ─────────────────────────────────
 *
 * Aquele devolve "há 3 dias", e é o certo onde a frase tem espaço — no painel,
 * ao lado de "dado defasado". Aqui são duas medidas na mesma linha de uma
 * coluna de 300px: "há 3 dias" ao lado da hora empurra a segunda para fora, e
 * o que some é justamente a mais usada.
 *
 * Sem espaço entre número e unidade, e sem "há": a coluna é varrida com o
 * olho, não lida.
 */
export function idadeCurta(instante: string | null | undefined, agora = Date.now()): string | null {
  if (!instante) return null;
  const quando = Date.parse(instante);
  if (Number.isNaN(quando)) return null;

  const atras = agora - quando;

  // Carimbo no futuro é relógio dessincronizado entre servidor e navegador, e
  // acontece. "em 3min" aqui faria duvidar da tela inteira.
  if (atras < 60_000) return 'agora';

  const minutos = Math.floor(atras / 60_000);
  if (minutos < 60) return `${minutos}min`;

  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `${horas}h`;

  const dias = Math.floor(horas / 24);
  if (dias < 14) return `${dias}d`;

  const semanas = Math.floor(dias / 7);
  if (dias < 60) return `${semanas}sem`;

  // Mês de 30 dias, e ano de 365: a conversa aberta há "4mes" não precisa de
  // calendário, precisa de ordem de grandeza.
  const meses = Math.floor(dias / 30);
  if (meses < 12) return `${meses}mes`;

  return `${Math.floor(dias / 365)}a`;
}

/**
 * Quando a conversa foi aberta, em palavras de gente.
 *
 * "hoje às 14:24" responde melhor que "01/09/2026" a pergunta que se faz
 * olhando um atendimento — que é "isto está esperando há quanto tempo?".
 */
export function abertaPorExtenso(iso: string): string {
  const quando = new Date(iso);
  const hora = new Intl.DateTimeFormat('pt-BR', { timeStyle: 'short' }).format(quando);
  const hoje = new Date();
  const mesmoDia = quando.toDateString() === hoje.toDateString();
  if (mesmoDia) return `hoje às ${hora}`;

  const ontem = new Date(hoje);
  ontem.setDate(hoje.getDate() - 1);
  if (quando.toDateString() === ontem.toDateString()) return `ontem às ${hora}`;

  const data = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(
    quando,
  );
  const anoDiferente = quando.getFullYear() !== hoje.getFullYear();
  return anoDiferente ? `em ${data}/${quando.getFullYear()} às ${hora}` : `em ${data} às ${hora}`;
}

/** O teto da máscara: R$ 9.999.999.999,99, doze dígitos. */
const MAIOR_EM_CENTAVOS = 12;

/**
 * A máscara de um campo em reais, como na maquininha: os dígitos entram pelos
 * centavos. Digitar 2, 5 e 0 dá "0,02", "0,25" e "2,50"; o ponto de milhar entra
 * sozinho ("1.234,56"). Sem dígito nenhum, o campo fica vazio.
 *
 * Sem ela, "250" lido por `emMicros` eram duzentos e cinquenta reais, e quem
 * queria R$ 2,50 só via o erro depois de salvar. O texto que sai continua no
 * formato que `emMicros` lê.
 */
export function mascararReais(digitado: string): string {
  const todos = digitado.replace(/\D/g, '');
  const digitos = todos.replace(/^0+/, '').slice(0, MAIOR_EM_CENTAVOS);
  if (digitos === '') {
    if (todos === '') return '';
    // Só zeros. O "0" digitado no campo vazio é o preço zero, de propósito; o
    // backspace em "0,00" ("0,0") esvazia o campo, senão ele nunca se apagaria.
    return digitado.includes(',') && digitado.length < '0,00'.length ? '' : '0,00';
  }
  const centavos = digitos.padStart(3, '0');
  const inteiros = centavos.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${inteiros},${centavos.slice(-2)}`;
}

/**
 * "12.500,50" e "12500.5" viram o mesmo número.
 *
 * O vendedor digita como fala, e o formato brasileiro usa vírgula. Aceitar só
 * ponto recusaria o que ele escreve; aceitar os dois sem regra transformaria
 * "12.500" em doze reais e meio — que é o erro que passa despercebido, porque
 * o número continua parecendo um número.
 *
 * A regra: se há vírgula, ela é o decimal e o ponto é separador de milhar.
 * Sem vírgula, o ponto é decimal quando sobram até dois dígitos depois dele.
 */
export function emMicros(bruto: string): number | null {
  const limpo = bruto.trim().replace(/\s|R\$/g, '');
  if (limpo === '') return null;

  let normalizado: string;
  if (limpo.includes(',')) {
    normalizado = limpo.replace(/\./g, '').replace(',', '.');
  } else {
    const partes = limpo.split('.');
    // "12.500" é doze mil e quinhentos; "12.50" é doze e cinquenta.
    normalizado =
      partes.length > 1 && (partes[partes.length - 1] as string).length === 3
        ? limpo.replace(/\./g, '')
        : limpo;
  }

  const numero = Number(normalizado);
  if (!Number.isFinite(numero) || numero < 0) return null;
  return Math.round(numero * 1_000_000);
}
